import { normalizeText } from "../../../utils/normalizeText";
// import { Icon } from '@iconify-icon/react/dist/iconify.mjs';
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./style.css";
import AppIcon from "../../App/AppIcon";

interface CustomMultiSelectProps {
  value: string[];
  onChange: (val: string[]) => void;
  options: { label: string; value: string }[];
  showClear?: boolean;
  showSearch?: boolean;
  className?: string;
  error?: string;
  disabled?: boolean;
  maxLabelLength?: number;
}

const CustomMultiSelect: React.FC<CustomMultiSelectProps> = ({
  value,
  onChange,
  options,
  showClear,
  showSearch,
  className = "",
  error,
  disabled,
  maxLabelLength = 50,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0, maxHeight: 0, openUpwards: false });

  const selectRef = useRef<HTMLDivElement>(null);
  const portalRef = useRef<HTMLUListElement>(null);

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const isSelected = (val: string) => value.includes(val);

  const handleSelect = (val: string) => {
    if (isSelected(val)) {
      onChange(value.filter((item) => item !== val));
    } else {
      onChange([...value, val]);
    }
  };

  const handleClear = () => {
    onChange([]);
    setIsOpen(false);
    setSearchQuery("");
  };

  const filteredOptions = options.filter((opt) => normalizeText(opt.label).includes(normalizeText(searchQuery)));

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (selectRef.current && !selectRef.current.contains(target) && portalRef.current && !portalRef.current.contains(target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && selectRef.current) {
      const updatePosition = () => {
        const rect = selectRef.current?.getBoundingClientRect();
        if (rect) {
          const viewportHeight = window.innerHeight;
          const spaceBelow = viewportHeight - rect.bottom;
          const spaceAbove = rect.top;
          const dropdownHeight = 300;
          
          const openUpwards = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
          const maxHeight = openUpwards 
            ? Math.min(spaceAbove - 10, dropdownHeight)
            : Math.min(spaceBelow - 10, dropdownHeight);
          
          setDropdownPosition({
            top: openUpwards ? rect.top : rect.bottom,
            left: rect.left,
            width: rect.width,
            maxHeight,
            openUpwards
          });
        }
      };

      updatePosition();
      const timeoutId = setTimeout(updatePosition, 10);

      return () => clearTimeout(timeoutId);
    }
  }, [isOpen]);

  return (
    <div className={`custom-multiselect-container ${className}`} ref={selectRef}>
      <div onClick={() => (disabled ? {} : setIsOpen(!isOpen))} className={`custom-multiselect-trigger ${disabled ? "disabled" : ""}`}>
        <span className={value.length > 0 ? "" : "custom-multiselect-placeholder"}>
          {value.length > 0 ? `${value.length} ${value.length > 1 ? "selecionados" : "selecionado"}` : "Selecione..."}
        </span>
        <div className="custom-multiselect-actions">
          {showClear && value.length > 0 && !disabled && (
            <button
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              className="custom-multiselect-clear"
            >
              <AppIcon icon={"mdi:clear"} />
            </button>
          )}
          <span>
            <AppIcon icon={"raphael:arrowdown"} width={12} />
          </span>
        </div>
      </div>

      {isOpen &&
        createPortal(
          <ul
            ref={portalRef}
            className={`custom-multiselect-dropdown ${dropdownPosition.openUpwards ? "open-upwards" : ""}`}
            style={{
              position: "fixed",
              top: dropdownPosition.openUpwards ? "auto" : dropdownPosition.top,
              bottom: dropdownPosition.openUpwards ? window.innerHeight - dropdownPosition.top : "auto",
              left: dropdownPosition.left,
              width: dropdownPosition.width || "auto",
              maxHeight: dropdownPosition.maxHeight,
              overflowY: "auto",
            }}
          >
            {showSearch && (
              <li className="custom-multiselect-search">
                <input type="text" placeholder="Buscar..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
              </li>
            )}
            {filteredOptions.map((opt) => (
              <li
                key={opt.value}
                onClick={() => handleSelect(opt.value)}
                className={`custom-multiselect-option ${isSelected(opt.value) ? "selected" : ""}`}
                style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                title={opt.label}
              >
                <input type="checkbox" checked={isSelected(opt.value)} readOnly tabIndex={-1} style={{ marginRight: 8 }} />
                {truncateText(opt.label, maxLabelLength)}
              </li>
            ))}
            <li className="custom-multiselect-counter">
              {value.length} {value.length === 1 ? "item selecionado" : "itens selecionados"}
            </li>
          </ul>,
          document.body
        )}
    </div>
  );
};

export default CustomMultiSelect;
